import React from 'react';
import { useLocation } from 'react-router-dom';
import PlusCircleIcon from './icons/PlusCircleIcon';
import ChartPieIcon from './icons/ChartPieIcon';
import ListBulletIcon from './icons/ListBulletIcon';
import UserCircleIcon from './icons/UserCircleIcon';
import { useTranslation } from 'react-i18next';

interface NavItemProps {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ label, icon, isActive, onClick }) => {
  const activeClasses = 'text-sky-600';
  const inactiveClasses = 'text-slate-500 hover:text-sky-600';

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center w-full pt-2 pb-1 transition-colors duration-200 ${isActive ? activeClasses : inactiveClasses}`}
    >
      {icon}
      <span className="text-xs font-medium mt-1">{label}</span>
    </button>
  );
};

interface BottomNavBarProps {
  onSolveClick: () => void;
  onDashboardClick: () => void;
  onHistoryClick: () => void;
  onAccountClick: () => void;
}

const BottomNavBar: React.FC<BottomNavBarProps> = ({
  onSolveClick,
  onDashboardClick,
  onHistoryClick,
  onAccountClick,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;

  const navItems = [
    { href: '/app/dashboard', label: t('bottomNavBar.dashboard'), icon: <ChartPieIcon className="w-6 h-6" />, onClick: onDashboardClick },
    { href: '/app/history', label: t('bottomNavBar.solutions'), icon: <ListBulletIcon className="w-6 h-6" />, onClick: onHistoryClick },
    { href: '/app/', label: t('bottomNavBar.solve'), icon: <PlusCircleIcon className="w-8 h-8" />, onClick: onSolveClick },
    { href: '/app/account', label: t('bottomNavBar.account'), icon: <UserCircleIcon className="w-6 h-6" />, onClick: onAccountClick },
  ];

  const isNavItemActive = (href: string) => {
    if (href === '/app/') {
      return ['/app/', '/app/configure', '/app/solution'].includes(pathname);
    }
    return pathname.startsWith(href);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-t border-slate-200 shadow-[-1px_0px_10px_rgba(0,0,0,0.1)] z-40 md:hidden print:hidden">
      <div className="max-w-md mx-auto flex justify-around items-center h-16">
        {navItems.map((item, index) => {
          if (item.label === t('bottomNavBar.solve')) {
            return (
              <div key={index} className="relative -top-5">
                <button
                  onClick={item.onClick}
                  className="bg-sky-600 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg hover:bg-sky-700 transition-all transform hover:scale-105 active:scale-100"
                  aria-label={t('bottomNavBar.solveNewAriaLabel')}
                >
                  {item.icon}
                </button>
              </div>
            );
          }
          return (
            <NavItem
              key={index}
              {...item}
              isActive={isNavItemActive(item.href)}
            />
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavBar;